import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_BRANCHES } from "../queries";
import {
  GET_REPORT_SCHEDULES,
  SAVE_REPORT_SCHEDULES,
  ReportFrequency,
  ReportMonthAnchor,
  BranchReportSchedule,
} from "./reportSchedulesQueries";
import { lang } from "../../../../middleware/lang";

// weekday follows the app-wide convention: 1=Sunday ... 7=Saturday
const WEEKDAYS: { value: number; es: string; en: string }[] = [
  { value: 1, es: "Domingo", en: "Sunday" },
  { value: 2, es: "Lunes", en: "Monday" },
  { value: 3, es: "Martes", en: "Tuesday" },
  { value: 4, es: "Miércoles", en: "Wednesday" },
  { value: 5, es: "Jueves", en: "Thursday" },
  { value: 6, es: "Viernes", en: "Friday" },
  { value: 7, es: "Sábado", en: "Saturday" },
];

type FormState = {
  daily: { enabled: boolean };
  weekly: { enabled: boolean; weekday: number };
  monthly: { enabled: boolean; month_anchor: ReportMonthAnchor };
};

// All frequencies default to on, matching the backend seeding (BranchService.createBranch
// and the branch_report_schedules backfill) so a branch with no saved rows yet still shows
// the actual default instead of appearing as fully opted out.
const defaultForm: FormState = {
  daily: { enabled: true },
  weekly: { enabled: true, weekday: 1 },
  monthly: { enabled: true, month_anchor: "last" },
};

const mapSchedulesToForm = (schedules: BranchReportSchedule[]): FormState => {
  if (schedules.length === 0) return defaultForm;

  const form: FormState = {
    daily: { enabled: false },
    weekly: { enabled: false, weekday: 1 },
    monthly: { enabled: false, month_anchor: "last" },
  };
  schedules.forEach((s) => {
    if (s.frequency === "daily") {
      form.daily.enabled = s.enabled;
    } else if (s.frequency === "weekly") {
      form.weekly.enabled = s.enabled;
      if (s.weekday) form.weekly.weekday = s.weekday;
    } else if (s.frequency === "monthly") {
      form.monthly.enabled = s.enabled;
      if (s.month_anchor) form.monthly.month_anchor = s.month_anchor;
    }
  });
  return form;
};

const mapFormToSchedules = (form: FormState) => {
  const schedules: {
    frequency: ReportFrequency;
    enabled: boolean;
    weekday?: number | null;
    month_anchor?: ReportMonthAnchor | null;
  }[] = [];

  if (form.daily.enabled) {
    schedules.push({ frequency: "daily", enabled: true });
  }
  if (form.weekly.enabled) {
    schedules.push({
      frequency: "weekly",
      enabled: true,
      weekday: form.weekly.weekday,
    });
  }
  if (form.monthly.enabled) {
    schedules.push({
      frequency: "monthly",
      enabled: true,
      month_anchor: form.monthly.month_anchor,
    });
  }
  return schedules;
};

const ReportSchedules: React.FC = () => {
  const [selectedBranch, setSelectedBranch] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [alertMessage, setAlertMessage] = useState<{
    type: "success" | "danger";
    message: string;
  } | null>(null);

  const { data: branchesData } = useQuery(GET_BRANCHES);
  const { data: schedulesData, loading, refetch } = useQuery(
    GET_REPORT_SCHEDULES,
    {
      variables: { branch_id: selectedBranch },
      skip: !selectedBranch,
      fetchPolicy: "no-cache",
    }
  );
  const [saveReportSchedules] = useMutation(SAVE_REPORT_SCHEDULES);

  useEffect(() => {
    if (branchesData?.getBranches?.length && !selectedBranch) {
      setSelectedBranch(branchesData.getBranches[0].id);
    }
  }, [branchesData, selectedBranch]);

  useEffect(() => {
    setForm(mapSchedulesToForm(schedulesData?.getReportSchedules || []));
  }, [schedulesData]);

  const clearAlert = () => setAlertMessage(null);

  const handleSave = async () => {
    if (!selectedBranch) return;
    setSaving(true);
    try {
      await saveReportSchedules({
        variables: {
          branch_id: selectedBranch,
          schedules: mapFormToSchedules(form),
        },
      });
      await refetch();
      setAlertMessage({
        type: "success",
        message: lang(
          "Preferencias de reportes guardadas",
          "Report preferences saved"
        ),
      });
    } catch (error) {
      console.error("Error saving report schedules:", error);
      setAlertMessage({
        type: "danger",
        message: lang(
          "Error al guardar las preferencias",
          "Error saving preferences"
        ),
      });
    }
    setSaving(false);
  };

  return (
    <div className="row justify-content-start mt-3">
      <div className="col col-md-8">
        <div className="card mb-2">
          {alertMessage && (
            <div
              className={`alert alert-${alertMessage.type} alert-dismissible fade show m-3`}
              role="alert"
            >
              {alertMessage.message}
              <button
                type="button"
                className="close"
                onClick={clearAlert}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          )}
          <div className="card-body">
            <h4>{lang("Reportes de Ventas por Correo", "Sales Report Emails")}</h4>
            <p className="text-muted">
              {lang(
                "Elige cuándo quieres recibir un resumen de ventas por correo para esta sucursal. Puedes activar varias opciones a la vez.",
                "Choose when you want to receive a sales summary email for this branch. You can turn on more than one option at a time."
              )}
            </p>

            <div className="form-group">
              <label>{lang("Sucursal", "Branch")}</label>
              <select
                className="form-control"
                value={selectedBranch || ""}
                onChange={(e) => setSelectedBranch(parseInt(e.target.value))}
              >
                {branchesData?.getBranches?.map((branch: any) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name || branch.address}
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="text-center py-3">
                <i className="fas fa-spinner fa-spin"></i>{" "}
                {lang("Cargando...", "Loading...")}
              </div>
            ) : (
              <div className="mt-3">
                {/* Daily */}
                <div className="form-group border rounded p-3 mb-3">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="report_daily_enabled"
                      checked={form.daily.enabled}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          daily: { enabled: e.target.checked },
                        })
                      }
                    />
                    <label
                      className="custom-control-label font-weight-bold"
                      htmlFor="report_daily_enabled"
                    >
                      {lang("Reporte diario", "Daily report")}
                    </label>
                  </div>
                  <small className="form-text text-muted ml-4 pl-1">
                    {lang(
                      "Se envía cada día, 1 hora después de la hora de cierre de la sucursal. No se envía en días cerrados ni si no hubo ventas.",
                      "Sent every day, 1 hour after the branch's closing time. Not sent on closed days or days with no sales."
                    )}
                  </small>
                </div>

                {/* Weekly */}
                <div className="form-group border rounded p-3 mb-3">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="report_weekly_enabled"
                      checked={form.weekly.enabled}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          weekly: { ...form.weekly, enabled: e.target.checked },
                        })
                      }
                    />
                    <label
                      className="custom-control-label font-weight-bold"
                      htmlFor="report_weekly_enabled"
                    >
                      {lang("Reporte semanal", "Weekly report")}
                    </label>
                  </div>
                  <small className="form-text text-muted ml-4 pl-1 mb-2 d-block">
                    {lang(
                      "Resumen de los últimos 7 días, enviado el día que elijas.",
                      "Summary of the last 7 days, sent on the day you choose."
                    )}
                  </small>
                  {form.weekly.enabled && (
                    <select
                      className="form-control ml-4"
                      style={{ width: "auto" }}
                      value={form.weekly.weekday}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          weekly: {
                            ...form.weekly,
                            weekday: parseInt(e.target.value),
                          },
                        })
                      }
                    >
                      {WEEKDAYS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {lang(d.es, d.en)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Monthly */}
                <div className="form-group border rounded p-3 mb-3">
                  <div className="custom-control custom-switch">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="report_monthly_enabled"
                      checked={form.monthly.enabled}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          monthly: {
                            ...form.monthly,
                            enabled: e.target.checked,
                          },
                        })
                      }
                    />
                    <label
                      className="custom-control-label font-weight-bold"
                      htmlFor="report_monthly_enabled"
                    >
                      {lang("Reporte mensual", "Monthly report")}
                    </label>
                  </div>
                  <small className="form-text text-muted ml-4 pl-1 mb-2 d-block">
                    {lang(
                      "Resumen del mes anterior completo.",
                      "Summary of the previous complete month."
                    )}
                  </small>
                  {form.monthly.enabled && (
                    <div className="ml-4">
                      <div className="custom-control custom-radio">
                        <input
                          type="radio"
                          id="month_anchor_first"
                          name="month_anchor"
                          className="custom-control-input"
                          checked={form.monthly.month_anchor === "first"}
                          onChange={() =>
                            setForm({
                              ...form,
                              monthly: {
                                ...form.monthly,
                                month_anchor: "first",
                              },
                            })
                          }
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="month_anchor_first"
                        >
                          {lang(
                            "Primer día del mes",
                            "First day of the month"
                          )}
                        </label>
                      </div>
                      <div className="custom-control custom-radio">
                        <input
                          type="radio"
                          id="month_anchor_last"
                          name="month_anchor"
                          className="custom-control-input"
                          checked={form.monthly.month_anchor === "last"}
                          onChange={() =>
                            setForm({
                              ...form,
                              monthly: {
                                ...form.monthly,
                                month_anchor: "last",
                              },
                            })
                          }
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="month_anchor_last"
                        >
                          {lang("Último día del mes", "Last day of the month")}
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              type="button"
              className="btn btn-success btn-block mt-4"
              onClick={handleSave}
              disabled={saving || loading || !selectedBranch}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>{" "}
                  {lang("Guardando...", "Saving...")}
                </>
              ) : (
                lang("Guardar", "Save")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportSchedules;
